'use client';

import { c as _c } from "react/compiler-runtime";
import { type GetTradesRow, hashDiagnosticEntity, useActiveWallet, useAuth, useGetTrades } from '@morfi/backend-sdk-v2/client';
import { useEffect, useRef } from 'react';
import type { MarketChartTrade } from '@/components/chart/market-chart';
import { useLiveListData } from '@/hooks/sdk/use-live-list-data';
import { mapTradeToAnnotation } from '@/lib/chart/sdk-mapping';
import { VIEWER_TRADE_PROJECTION } from '@/lib/sdk/projections';
import { viewerMarketTradeQueryArgs } from '@/lib/sdk/viewer-market-trade-query';
import { logger } from '@/lib/telemetry/logger';
interface WinnerTradeMarkerScope {
  readonly marketPubkey: string;
  readonly outcomeMint: string;
  readonly originalOutcomeIndex: number;
  readonly tradesFrom: string;
}
export function useWinnerTradeMarkers(t0) {
  const $ = _c(25);
  const {
    marketPubkey,
    outcomeMint,
    originalOutcomeIndex,
    tradesFrom
  } = t0;
  const {
    isAuthenticated
  } = useAuth();
  const {
    activeWallet
  } = useActiveWallet();
  const viewerWallet = isAuthenticated ? activeWallet?.address : undefined;
  const enabled = viewerWallet !== undefined;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [...VIEWER_TRADE_PROJECTION];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = viewerWallet ?? "";
  const t3 = String(originalOutcomeIndex);
  let t4;
  if ($[1] !== marketPubkey || $[2] !== outcomeMint || $[3] !== t2 || $[4] !== t3 || $[5] !== tradesFrom) {
    t4 = viewerMarketTradeQueryArgs({
      market: marketPubkey,
      from: tradesFrom,
      user: t2,
      outcomeIndex: t3,
      mints: [outcomeMint]
    });
    $[1] = marketPubkey;
    $[2] = outcomeMint;
    $[3] = t2;
    $[4] = t3;
    $[5] = tradesFrom;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t4) {
    t5 = {
      fields: t1,
      ...t4
    };
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== enabled) {
    t6 = {
      enabled,
      eagerSnapshot: true
    };
    $[9] = enabled;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const query = useGetTrades(t5, t6);
  const rows = useLiveListData(query.queryKey, query.rows, query.entityQueryKey, query.projectEntityRow, enabled);
  let t7;
  if ($[11] !== originalOutcomeIndex || $[12] !== rows || $[13] !== viewerWallet) {
    t7 = mapWinnerTradeMarkers(rows, viewerWallet, originalOutcomeIndex);
    $[11] = originalOutcomeIndex;
    $[12] = rows;
    $[13] = viewerWallet;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const {
    markers,
    invalidRows
  } = t7;
  let t8;
  if ($[15] !== marketPubkey) {
    t8 = hashDiagnosticEntity(marketPubkey);
    $[15] = marketPubkey;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const marketHash = t8;
  let t9;
  if ($[17] !== outcomeMint) {
    t9 = hashDiagnosticEntity(outcomeMint);
    $[17] = outcomeMint;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const mintHash = t9;
  useWinnerMarkerPagination(query, enabled, marketHash, mintHash);
  const queryError = enabled && query.isError ? query.error?.name ?? "unknown" : null;
  const warnedFailure = useRef(null);
  let t10;
  let t11;
  if ($[19] !== invalidRows || $[20] !== marketHash || $[21] !== mintHash || $[22] !== queryError) {
    t10 = () => {
      const fingerprint = `${marketHash}|${mintHash}|${queryError}|${invalidRows}`;
      if (queryError === null && invalidRows === 0) {
        warnedFailure.current = null;
        return;
      }
      if (warnedFailure.current === fingerprint) {
        return;
      }
      warnedFailure.current = fingerprint;
      if (queryError !== null) {
        logger.warn("winner_detail.trade_overlay_unavailable", {
          stage: "query",
          scope: "viewer",
          retryable: true,
          errorClass: queryError,
          marketHash,
          mintHash
        });
      }
      if (invalidRows > 0) {
        logger.warn("winner_detail.trade_overlay_row_dropped", {
          marketHash,
          mintHash,
          droppedCount: invalidRows
        });
      }
    };
    t11 = [invalidRows, marketHash, mintHash, queryError];
    $[19] = invalidRows;
    $[20] = marketHash;
    $[21] = mintHash;
    $[22] = queryError;
    $[23] = t10;
    $[24] = t11;
  } else {
    t10 = $[23];
    t11 = $[24];
  }
  useEffect(t10, t11);
  return markers;
}
function useWinnerMarkerPagination(query, enabled, marketHash, mintHash) {
  const $ = _c(8);
  const {
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = query;
  const warnedErrorClass = useRef(null);
  let t0;
  let t1;
  if ($[0] !== enabled || $[1] !== fetchNextPage || $[2] !== hasNextPage || $[3] !== isFetchingNextPage || $[4] !== marketHash || $[5] !== mintHash) {
    t0 = () => {
      if (!enabled || !hasNextPage || isFetchingNextPage) {
        return;
      }
      fetchNextPage().catch(error => {
        const errorClass = error instanceof Error ? error.name : "unknown";
        if (warnedErrorClass.current === errorClass) {
          return;
        }
        warnedErrorClass.current = errorClass;
        logger.warn("winner_detail.trade_overlay_unavailable", {
          stage: "pagination",
          scope: "viewer",
          retryable: true,
          errorClass,
          marketHash,
          mintHash
        });
      });
    };
    t1 = [enabled, fetchNextPage, hasNextPage, isFetchingNextPage, marketHash, mintHash];
    $[0] = enabled;
    $[1] = fetchNextPage;
    $[2] = hasNextPage;
    $[3] = isFetchingNextPage;
    $[4] = marketHash;
    $[5] = mintHash;
    $[6] = t0;
    $[7] = t1;
  } else {
    t0 = $[6];
    t1 = $[7];
  }
  useEffect(t0, t1);
}
function mapWinnerTradeMarkers(rows: ReadonlyArray<GetTradesRow>, viewerWallet: string | undefined, originalOutcomeIndex: number): {
  markers: MarketChartTrade[];
  invalidRows: number;
} {
  const markers: MarketChartTrade[] = [];
  let invalidRows = 0;
  if (viewerWallet === undefined) return {
    markers,
    invalidRows
  };
  for (const row of rows) {
    // Realtime subjects are shared; reject another viewer/outcome locally too.
    if (row.trader?.walletAddress !== viewerWallet || row.outcomeIndex !== originalOutcomeIndex) {
      continue;
    }
    const trade = mapTradeToAnnotation(row, 0);
    if (trade.signature === '' || !Number.isFinite(trade.timestamp) || trade.side === undefined || !Number.isFinite(trade.usdcAmount) || trade.usdcAmount <= 0) {
      invalidRows += 1;
      continue;
    }
    markers.push({
      id: trade.signature,
      time: Number(trade.timestamp),
      // Winner detail renders the original winner as its sole local series.
      outcomeIndex: 0,
      side: trade.side,
      quoteAmount: trade.usdcAmount
    });
  }
  return {
    markers,
    invalidRows
  };
}